import { supabase } from "@/api/supabaseClient";

export type RhythmKind = "interval" | "daily_times" | "once" | "as_needed";

export type TherapyRating =
  | "recommended"
  | "maybe"
  | "not_recommended"
  | "contraindicated"
  | "unrated";

export type MedicationRow = {
  id: string;
  organization_id: string;
  code: string | null;
  name: string;
  substance: string | null;
  form: string | null;
  default_route: string | null;
  dosing_notes: string | null;
  weight_based_rules: unknown;
  typical_duration_text: string | null;
  typical_rhythm: unknown;
  warnings: string | null;
  source_text: string | null;
  reviewed_at: string | null;
  is_active: boolean;
};

export type TreatmentRow = {
  id: string;
  organization_id: string;
  code: string | null;
  name: string;
  description: string | null;
  typical_duration_text: string | null;
  typical_rhythm: unknown;
  warnings: string | null;
  source_text: string | null;
  reviewed_at: string | null;
  is_active: boolean;
};

export type CatalogReviewItem = {
  id: string;
  organization_id: string;
  kind: "medication" | "treatment";
  free_name: string;
  source_entry_id: string;
  source_event_id: string | null;
  status: "open" | "accepted" | "rejected";
  resolved_medication_id: string | null;
  resolved_treatment_id: string | null;
  resolution_note: string | null;
  created_at: string;
};

export type DiagnosisTherapyRatingRow = {
  id: string;
  organization_id: string;
  diagnosis_id: string;
  medication_id: string | null;
  treatment_id: string | null;
  rating: TherapyRating;
  notes: string | null;
};

export type TherapySidebar = {
  plans: Array<{
    plan_id: string;
    status: string;
    revision_no: number;
    therapy_kind: "medication" | "treatment";
    medication_id: string | null;
    treatment_id: string | null;
    label: string;
    dose_amount: number | null;
    dose_unit: string | null;
    route: string | null;
    rhythm_kind: RhythmKind;
    rhythm: Record<string, unknown>;
    rhythm_note: string | null;
    started_at: string;
    planned_end_at: string | null;
  }>;
  hints: Array<{
    hint_id: string;
    body: string;
    is_warning: boolean;
    status: string;
  }>;
};

export type PlanDue = {
  plan_id: string;
  plan_revision_id: string;
  revision_no: number;
  planned_at: string;
  occurrence_key: string;
  therapy_kind: "medication" | "treatment";
  label: string;
};

const MED_SELECT =
  "id, organization_id, code, name, substance, form, default_route, dosing_notes, weight_based_rules, typical_duration_text, typical_rhythm, warnings, source_text, reviewed_at, is_active";
const TRT_SELECT =
  "id, organization_id, code, name, description, typical_duration_text, typical_rhythm, warnings, source_text, reviewed_at, is_active";

export class TherapyApi {
  static async listMedications(
    organizationId: string,
    opts?: { includeInactive?: boolean }
  ): Promise<MedicationRow[]> {
    let q = supabase
      .from("medications")
      .select(MED_SELECT)
      .eq("organization_id", organizationId)
      .order("name");
    if (!opts?.includeInactive) q = q.eq("is_active", true);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as MedicationRow[];
  }

  static async listTreatments(
    organizationId: string,
    opts?: { includeInactive?: boolean }
  ): Promise<TreatmentRow[]> {
    let q = supabase
      .from("treatments")
      .select(TRT_SELECT)
      .eq("organization_id", organizationId)
      .order("name");
    if (!opts?.includeInactive) q = q.eq("is_active", true);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as TreatmentRow[];
  }

  static async upsertMedication(
    input: Partial<MedicationRow> & {
      organization_id: string;
      name: string;
    }
  ): Promise<MedicationRow> {
    const payload = {
      organization_id: input.organization_id,
      code: input.code ?? null,
      name: input.name.trim(),
      substance: input.substance ?? null,
      form: input.form ?? null,
      default_route: input.default_route ?? null,
      dosing_notes: input.dosing_notes ?? null,
      weight_based_rules: input.weight_based_rules ?? [],
      typical_duration_text: input.typical_duration_text ?? null,
      typical_rhythm: input.typical_rhythm ?? null,
      warnings: input.warnings ?? null,
      source_text: input.source_text ?? null,
      is_active: input.is_active ?? true,
      ...(input.id ? { id: input.id } : {}),
    };
    const { data, error } = await supabase
      .from("medications")
      .upsert(payload)
      .select(MED_SELECT)
      .single();
    if (error) throw error;
    return data as MedicationRow;
  }

  static async upsertTreatment(
    input: Partial<TreatmentRow> & {
      organization_id: string;
      name: string;
    }
  ): Promise<TreatmentRow> {
    const payload = {
      organization_id: input.organization_id,
      code: input.code ?? null,
      name: input.name.trim(),
      description: input.description ?? null,
      typical_duration_text: input.typical_duration_text ?? null,
      typical_rhythm: input.typical_rhythm ?? null,
      warnings: input.warnings ?? null,
      source_text: input.source_text ?? null,
      is_active: input.is_active ?? true,
      ...(input.id ? { id: input.id } : {}),
    };
    const { data, error } = await supabase
      .from("treatments")
      .upsert(payload)
      .select(TRT_SELECT)
      .single();
    if (error) throw error;
    return data as TreatmentRow;
  }

  static async setMedicationActive(id: string, isActive: boolean) {
    const { error } = await supabase
      .from("medications")
      .update({ is_active: isActive })
      .eq("id", id);
    if (error) throw error;
  }

  static async setTreatmentActive(id: string, isActive: boolean) {
    const { error } = await supabase
      .from("treatments")
      .update({ is_active: isActive })
      .eq("id", id);
    if (error) throw error;
  }

  static async listOpenReviews(
    organizationId: string
  ): Promise<CatalogReviewItem[]> {
    const { data, error } = await supabase
      .from("catalog_review_items")
      .select(
        "id, organization_id, kind, free_name, source_entry_id, source_event_id, status, resolved_medication_id, resolved_treatment_id, resolution_note, created_at"
      )
      .eq("organization_id", organizationId)
      .eq("status", "open")
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data ?? []) as CatalogReviewItem[];
  }

  static async resolveReview(input: {
    reviewId: string;
    status: "accepted" | "rejected";
    medicationId?: string | null;
    treatmentId?: string | null;
    note?: string | null;
  }) {
    const { data, error } = await supabase.rpc("resolve_catalog_review_item", {
      p_review_id: input.reviewId,
      p_status: input.status,
      p_medication_id: input.medicationId ?? null,
      p_treatment_id: input.treatmentId ?? null,
      p_note: input.note ?? null,
    });
    if (error) throw error;
    return data;
  }

  static async listDiagnosisTherapyRatings(
    organizationId: string
  ): Promise<DiagnosisTherapyRatingRow[]> {
    const { data, error } = await supabase
      .from("diagnosis_therapy_ratings")
      .select(
        "id, organization_id, diagnosis_id, medication_id, treatment_id, rating, notes"
      )
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as DiagnosisTherapyRatingRow[];
  }

  static async upsertDiagnosisTherapyRating(input: {
    id?: string;
    organization_id: string;
    diagnosis_id: string;
    medication_id?: string | null;
    treatment_id?: string | null;
    rating: TherapyRating;
    notes?: string | null;
  }): Promise<DiagnosisTherapyRatingRow> {
    const payload = {
      organization_id: input.organization_id,
      diagnosis_id: input.diagnosis_id,
      medication_id: input.medication_id ?? null,
      treatment_id: input.treatment_id ?? null,
      rating: input.rating,
      notes: input.notes?.trim() || null,
      ...(input.id ? { id: input.id } : {}),
    };
    const { data, error } = await supabase
      .from("diagnosis_therapy_ratings")
      .upsert(payload)
      .select(
        "id, organization_id, diagnosis_id, medication_id, treatment_id, rating, notes"
      )
      .single();
    if (error) throw error;
    return data as DiagnosisTherapyRatingRow;
  }

  static async deleteDiagnosisTherapyRating(id: string) {
    const { error } = await supabase
      .from("diagnosis_therapy_ratings")
      .delete()
      .eq("id", id);
    if (error) throw error;
  }

  static async listSidebar(caseId: string): Promise<TherapySidebar> {
    const { data, error } = await supabase.rpc("list_case_therapy_sidebar", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? { plans: [], hints: [] }) as TherapySidebar;
  }

  static async previewDues(
    caseId: string,
    asOf?: string,
    horizonHours = 72
  ): Promise<PlanDue[]> {
    const { data, error } = await supabase.rpc("preview_plan_dues", {
      p_case_id: caseId,
      p_as_of: asOf ?? new Date().toISOString(),
      p_horizon_hours: horizonHours,
    });
    if (error) throw error;
    return (data ?? []) as PlanDue[];
  }
}
